import React, { useState, Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowUpRight, ArrowDownRight, Users, Heart, Target, Wallet as WalletIcon, ShieldCheck, X, Zap, ArrowLeftRight, CreditCard, Hash, Smartphone, CheckCircle2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatCard } from '../components/ui/StatCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { toast } from 'sonner';
export const Overview: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  const navigate = useNavigate();
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundPaymentMethod, setFundPaymentMethod] = useState('card');
  const [isKycBannerDismissed, setIsKycBannerDismissed] = useState(false);
  const {
    user,
    transactions,
    campaigns
  } = state;
  const kycTier = user?.kyc?.tier || 0;
  const showKycBanner = kycTier < 3 && !isKycBannerDismissed;
  const kycBannerContent = {
    0: {
      message: 'Your account is unverified. Complete KYC to start transacting.',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      icon: 'text-orange-500',
      btn: 'bg-orange-600 hover:bg-orange-700 text-white'
    },
    1: {
      message: 'Upgrade to Standard (Tier 2) for ₦200,000 daily limit.',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: 'text-blue-500',
      btn: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    2: {
      message: 'Upgrade to Premium (Tier 3) for unlimited transactions.',
      bg: 'bg-brand-primary/10',
      border: 'border-brand-primary/30',
      text: 'text-brand-primary-dark',
      icon: 'text-brand-primary',
      btn: 'bg-brand-primary hover:bg-brand-primary-dark text-white'
    }
  }[kycTier as 0 | 1 | 2];
  // Calculate crowdfunding-focused stats
  const activeCampaigns = campaigns.filter(c => c.status === 'Active').length;
  const totalRaised = campaigns.reduce((sum, c) => sum + c.raisedAmount, 0);
  const totalDonations = transactions.filter(t => t.type === 'Crowdfunding' && t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalDonors = campaigns.reduce((sum, c) => sum + (c.raisedAmount > 0 ? 1 : 0), 0);
  // Calculate Total In and Total Out for Wallet Card
  const totalIn = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
  const totalOut = Math.abs(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0));
  // Filter recent transactions to crowdfunding and wallet only
  const recentTransactions = transactions.filter(t => t.type === 'Crowdfunding' || t.type === 'Wallet').slice(0, 5);
  // Crowdfunding-focused chart data
  const chartData = [{
    name: 'Jan',
    Donations: 15000,
    Raised: 45000
  }, {
    name: 'Feb',
    Donations: 8000,
    Raised: 32000
  }, {
    name: 'Mar',
    Donations: 22000,
    Raised: 67000
  }, {
    name: 'Apr',
    Donations: 12000,
    Raised: 41000
  }, {
    name: 'May',
    Donations: 18000,
    Raised: 55000
  }, {
    name: 'Jun',
    Donations: 25000,
    Raised: 72000
  }];
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type: 'Wallet',
        title: 'Card Funding',
        amount: amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference: `FND-${Date.now().toString().slice(-6)}`
      }
    });
    toast.success(`Successfully added ${formatCurrency(amount)} to your wallet`);
    setIsAddFundsOpen(false);
    setFundAmount('');
  };
  return <div className="space-y-6" data-id="element-1364">
      {/* KYC Banner */}
      <AnimatePresence data-id="element-1365">
        {showKycBanner && kycBannerContent && <motion.div initial={{
        opacity: 0,
        y: -20,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        scale: 0.95,
        height: 0,
        marginBottom: 0
      }} className={`relative overflow-hidden rounded-2xl border ${kycBannerContent.border} ${kycBannerContent.bg} p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm`} data-id="element-1366">
            <div className="flex items-start sm:items-center gap-4" data-id="element-1367">
              <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm ${kycBannerContent.icon}`} data-id="element-1368">
                <ShieldCheck size={24} data-id="element-1369" />
              </div>
              <div data-id="element-1370">
                <div className="flex items-center gap-2 mb-1" data-id="element-1371">
                  <h3 className={`font-bold ${kycBannerContent.text}`} data-id="element-1372">
                    KYC Verification
                  </h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-white ${kycBannerContent.text} shadow-sm uppercase tracking-wider`} data-id="element-1373">
                    Tier {kycTier}
                  </span>
                </div>
                <p className={`text-sm font-medium ${kycBannerContent.text} opacity-90`} data-id="element-1374">
                  {kycBannerContent.message}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0" data-id="element-1375">
              <button onClick={() => navigate('/dashboard/settings', {
            state: {
              activeSection: 'kyc'
            }
          })} className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-colors ${kycBannerContent.btn}`} data-id="element-1376">
                Verify Now
              </button>
              <button onClick={() => setIsKycBannerDismissed(true)} className={`p-2 rounded-lg hover:bg-white/50 transition-colors ${kycBannerContent.text} opacity-70 hover:opacity-100`} aria-label="Dismiss" data-id="element-1377">
                <X size={20} data-id="element-1378" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1379">
        <div data-id="element-1380">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-1381">
            Welcome back, {user?.name.split(' ')[0]}! 👋
          </h1>
          <p className="text-sm text-brand-text-light" data-id="element-1382">
            {user?.kyc?.verified ? 'Your account is verified' : 'Complete KYC to unlock full features'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3" data-id="element-1383">
          <button onClick={() => navigate('/dashboard/settings')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-colors" data-id="element-1384">
            <ShieldCheck size={18} className="opacity-80" data-id="element-1385" />
            <span className="text-sm font-medium" data-id="element-1386">Settings</span>
          </button>
          <button onClick={() => navigate('/dashboard/wallet')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-primary/10 backdrop-blur-md border border-brand-primary/20 text-white hover:bg-brand-primary/20 transition-colors" data-id="element-1387">
            <WalletIcon size={18} className="opacity-80" data-id="element-1388" />
            <span className="text-sm font-medium" data-id="element-1389">Wallet</span>
          </button>
        </div>
      </div>

      {/* Top Section: Wallet & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1390">
        {/* Modern Wallet Card */}
        <div className="lg:col-span-2 relative bg-gradient-to-br from-[#004d26] via-[#006633] to-[#0A1F0A] rounded-[2rem] p-8 text-white overflow-hidden shadow-xl shadow-brand-primary-dark/20 flex flex-col justify-between min-h-[240px]" data-id="element-1391">
          {/* Abstract Mesh/Glow Effects */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-primary/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-1392" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/4 pointer-events-none" data-id="element-1393" />

          {/* Watermark Logo */}
          <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none" data-id="element-1394">
            <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="" className="w-64 h-64 grayscale" data-id="element-1395" />
          </div>

          <div className="relative z-10" data-id="element-1396">
            <div className="flex justify-between items-start mb-2" data-id="element-1397">
              <p className="text-brand-primary-light/80 font-medium tracking-wide uppercase text-sm" data-id="element-1398">
                Total Balance
              </p>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium border border-white/10 flex items-center gap-1.5" data-id="element-1399">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary-light animate-pulse" data-id="element-1400"></span>
                Active
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6" data-id="element-1401">
              <h2 className="text-5xl sm:text-6xl font-bold tracking-tight text-white drop-shadow-md" data-id="element-1402">
                {formatCurrency(user?.walletBalance || 0)}
              </h2>
              <button onClick={() => setIsAddFundsOpen(true)} className="bg-brand-accent hover:bg-orange-400 text-white px-6 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-brand-accent/20 shrink-0" data-id="element-1403">
                <Plus size={20} data-id="element-1404" /> Add Funds
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm font-medium" data-id="element-1405">
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-1406">
                <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center" data-id="element-1407">
                  <ArrowDownRight size={14} className="text-green-300" data-id="element-1408" />
                </div>
                <div data-id="element-1409">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-1410">
                    Total In
                  </div>
                  <div className="text-white" data-id="element-1411">{formatCurrency(totalIn)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-1412">
                <div className="w-6 h-6 rounded-full bg-red-400/20 flex items-center justify-center" data-id="element-1413">
                  <ArrowUpRight size={14} className="text-red-300" data-id="element-1414" />
                </div>
                <div data-id="element-1415">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-1416">
                    Total Out
                  </div>
                  <div className="text-white" data-id="element-1417">{formatCurrency(totalOut)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm flex flex-col justify-center" data-id="element-1418">
          <h3 className="text-sm font-semibold text-brand-text-light uppercase tracking-wider mb-4" data-id="element-1419">
            Quick Actions
          </h3>
          <div className="grid grid-cols-3 gap-3" data-id="element-1420">
            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/crowdfunding')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1421">
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-500 flex items-center justify-center group-hover:bg-pink-500 group-hover:text-white transition-colors" data-id="element-1422">
                <Heart size={20} data-id="element-1423" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1424">
                Donate
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/crowdfunding')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1425">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors" data-id="element-1426">
                <Users size={20} data-id="element-1427" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1428">
                Fundraise
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/wallet')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1429">
              <div className="w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary-dark flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors" data-id="element-1430">
                <WalletIcon size={20} data-id="element-1431" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1432">
                Wallet
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/bills')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1433">
              <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-600 flex items-center justify-center group-hover:bg-yellow-500 group-hover:text-white transition-colors" data-id="element-1434">
                <Zap size={20} data-id="element-1435" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1436">
                Bill Payment
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/diaspora')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1437">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-500 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-colors" data-id="element-1438">
                <ArrowLeftRight size={20} data-id="element-1439" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1440">
                Exchange
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => setIsAddFundsOpen(true)} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1441">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-brand-accent flex items-center justify-center group-hover:bg-brand-accent group-hover:text-white transition-colors" data-id="element-1442">
                <Plus size={20} data-id="element-1443" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1444">
                Add Funds
              </span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1445">
        <StatCard title="Active Campaigns" value={activeCampaigns} icon={Users} colorClass="text-blue-600" data-id="element-1446" />
        <StatCard title="Total Raised" value={formatCurrency(totalRaised)} icon={Target} colorClass="text-brand-primary" data-id="element-1447" />
        <StatCard title="My Donations" value={formatCurrency(totalDonations)} icon={Heart} colorClass="text-pink-500" data-id="element-1448" />
        <StatCard title="Campaigns Supported" value={totalDonors} icon={Users} colorClass="text-brand-accent" data-id="element-1449" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1450">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-id="element-1451">
          <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1452">
            Crowdfunding Overview
          </h3>
          <div className="h-72 w-full" data-id="element-1453">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1454">
              <AreaChart data={chartData} margin={{
              top: 10,
              right: 0,
              left: -20,
              bottom: 0
            }} data-id="element-1455">
                <defs data-id="element-1456">
                  <linearGradient id="colorRaised" x1="0" y1="0" x2="0" y2="1" data-id="element-1457">
                    <stop offset="5%" stopColor="#73C883" stopOpacity={0.3} data-id="element-1458" />
                    <stop offset="95%" stopColor="#73C883" stopOpacity={0} data-id="element-1459" />
                  </linearGradient>
                  <linearGradient id="colorDonations" x1="0" y1="0" x2="0" y2="1" data-id="element-1460">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-1461" />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-1462" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-1463" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-1464" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={value => `₦${value / 1000}k`} data-id="element-1465" />
                <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => formatCurrency(value)} data-id="element-1466" />
                <Area type="monotone" dataKey="Raised" stroke="#73C883" strokeWidth={3} fillOpacity={1} fill="url(#colorRaised)" data-id="element-1467" />
                <Area type="monotone" dataKey="Donations" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorDonations)" data-id="element-1468" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col" data-id="element-1469">
          <div className="flex items-center justify-between mb-6" data-id="element-1470">
            <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-1471">
              Recent Activity
            </h3>
            <button onClick={() => navigate('/dashboard/wallet')} className="text-sm font-medium text-brand-primary hover:text-brand-primary-dark" data-id="element-1472">
              View All
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4" data-id="element-1473">
            {recentTransactions.length === 0 ? <div className="text-center py-8 text-brand-text-light" data-id="element-1474">
                <Heart size={32} className="mx-auto mb-3 opacity-40" data-id="element-1475" />
                <p className="text-sm" data-id="element-1476">No crowdfunding activity yet.</p>
                <p className="text-xs mt-1" data-id="element-1477">Start by supporting a campaign!</p>
              </div> : <motion.div initial="hidden" animate="visible" variants={{
            visible: {
              transition: {
                staggerChildren: 0.05
              }
            }
          }} className="space-y-3" data-id="element-1478">
                {recentTransactions.map(tx => <motion.div key={tx.id} variants={{
              hidden: {
                opacity: 0,
                y: 10
              },
              visible: {
                opacity: 1,
                y: 0
              }
            }} className="flex items-center justify-between p-3 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-gray-50 transition-all group" data-id="element-1479">
                    <div className="flex items-center gap-3" data-id="element-1480">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-1481">
                        {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-1482" /> : <ArrowUpRight size={20} data-id="element-1483" />}
                      </div>
                      <div className="overflow-hidden" data-id="element-1484">
                        <p className="text-sm font-semibold text-brand-text-dark truncate" data-id="element-1485">
                          {tx.title}
                        </p>
                        <p className="text-xs text-brand-text-light" data-id="element-1486">
                          {formatDate(tx.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0" data-id="element-1487">
                      <p className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-1488">
                        {tx.amount > 0 ? '+' : ''}
                        {formatCurrency(tx.amount)}
                      </p>
                      <div className="mt-1 flex justify-end" data-id="element-1489">
                        <StatusBadge status={tx.status} data-id="element-1490" />
                      </div>
                    </div>
                  </motion.div>)}
              </motion.div>}
          </div>
        </div>
      </div>

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-1491">
        <form onSubmit={handleAddFunds} className="space-y-5" data-id="element-1492">
          <div data-id="element-1493">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1494">
              Amount (₦)
            </label>
            <div className="relative" data-id="element-1495">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-1496">
                ₦
              </span>
              <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold transition-all" min="1000" required data-id="element-1497" />
            </div>
            <p className="text-xs text-gray-500 mt-2" data-id="element-1498">
              Minimum amount is ₦1,000
            </p>
          </div>

          <div data-id="element-1499">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1500">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-1501">
              <button type="button" onClick={() => setFundPaymentMethod('card')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'card' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1502">
                <CreditCard size={20} data-id="element-1503" />
                <span className="text-xs font-semibold" data-id="element-1504">Card</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('ussd')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'ussd' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1505">
                <Hash size={20} data-id="element-1506" />
                <span className="text-xs font-semibold" data-id="element-1507">USSD</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('wallet')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'wallet' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1508">
                <WalletIcon size={20} data-id="element-1509" />
                <span className="text-xs font-semibold" data-id="element-1510">Wallet</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('google_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'google_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1511">
                <div className="flex items-center gap-1 font-bold text-gray-800 text-sm" data-id="element-1512">
                  <span className="text-blue-500" data-id="element-1513">G</span>Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-1514">Google Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('apple_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'apple_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1515">
                <div className="font-bold text-gray-800 text-sm flex items-center" data-id="element-1516">
                   Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-1517">Apple Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('mobile_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'mobile_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1518">
                <Smartphone size={20} data-id="element-1519" />
                <span className="text-xs font-semibold" data-id="element-1520">Mobile Pay</span>
              </button>
            </div>

            {fundPaymentMethod === 'card' ? <div className="space-y-3 animate-fade-in" data-id="element-1521">
                <div className="flex items-center gap-2 mb-2" data-id="element-1522">
                  <div className="h-6 px-2 bg-blue-50 rounded flex items-center justify-center text-blue-800 font-bold italic text-xs border border-blue-100" data-id="element-1523">
                    VISA
                  </div>
                  <div className="h-6 px-2 bg-gray-50 rounded flex items-center justify-center border border-gray-200" data-id="element-1524">
                    <div className="flex -space-x-1.5" data-id="element-1525">
                      <div className="w-3 h-3 rounded-full bg-red-500 mix-blend-multiply" data-id="element-1526"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mix-blend-multiply" data-id="element-1527"></div>
                    </div>
                  </div>
                  <div className="h-6 px-2 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-[10px] tracking-tighter" data-id="element-1528">
                    AMEX
                  </div>
                  <div className="h-6 px-2 bg-red-600 rounded flex items-center justify-center text-white font-bold italic text-[10px]" data-id="element-1529">
                    Verve
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm" data-id="element-1530">
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-600" data-id="element-1531">
                    <CreditCard size={20} data-id="element-1532" />
                  </div>
                  <div className="flex-1" data-id="element-1533">
                    <p className="text-sm font-semibold text-brand-text-dark" data-id="element-1534">
                      Saved Debit Card
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1535">•••• 4242</p>
                  </div>
                  <CheckCircle2 className="text-brand-primary" size={20} data-id="element-1536" />
                </div>
              </div> : fundPaymentMethod === 'ussd' ? <div className="space-y-4 animate-fade-in" data-id="element-1537">
                <div data-id="element-1538">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1539">
                    Select Bank for USSD
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none bg-white" data-id="element-1540">
                    <option value="*737#" data-id="element-1541">GTBank (*737#)</option>
                    <option value="*901#" data-id="element-1542">Access Bank (*901#)</option>
                    <option value="*770#" data-id="element-1543">Fidelity Bank (*770#)</option>
                    <option value="*894#" data-id="element-1544">First Bank (*894#)</option>
                    <option value="*822#" data-id="element-1545">Sterling Bank (*822#)</option>
                    <option value="*919#" data-id="element-1546">UBA (*919#)</option>
                    <option value="*966#" data-id="element-1547">Zenith Bank (*966#)</option>
                  </select>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center" data-id="element-1548">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-1549">
                    Dial the code below on your mobile phone to complete the
                    payment.
                  </p>
                  <div className="text-2xl font-bold text-brand-text-dark tracking-wider" data-id="element-1550">
                    *737*000*1234#
                  </div>
                </div>
              </div> : fundPaymentMethod === 'wallet' ? <div className="space-y-4 animate-fade-in" data-id="element-1551">
                <div className="bg-brand-primary-dark/5 p-4 rounded-xl border border-brand-primary/20 text-center" data-id="element-1552">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-1553">
                    Current Wallet Balance
                  </p>
                  <div className="text-2xl font-bold text-brand-primary-dark" data-id="element-1554">
                    {formatCurrency(state.user?.walletBalance || 0)}
                  </div>
                  <p className="text-xs text-brand-primary mt-2 flex items-center justify-center gap-1" data-id="element-1555">
                    <CheckCircle2 size={14} data-id="element-1556" /> Sufficient balance available
                  </p>
                </div>
              </div> : <div className="space-y-4 animate-fade-in" data-id="element-1557">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center" data-id="element-1558">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-1559">
                    {fundPaymentMethod === 'google_pay' && <span className="font-bold text-xl" data-id="element-1560">
                        <span className="text-blue-500" data-id="element-1561">G</span>Pay
                      </span>}
                    {fundPaymentMethod === 'apple_pay' && <span className="font-bold text-2xl" data-id="element-1562"></span>}
                    {fundPaymentMethod === 'mobile_pay' && <Smartphone size={32} className="text-gray-700" data-id="element-1563" />}
                  </div>
                  <h4 className="font-semibold text-brand-text-dark mb-2" data-id="element-1564">
                    Pay with{' '}
                    {fundPaymentMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-1565">
                    You will be redirected to complete your{' '}
                    {fundAmount ? formatCurrency(Number(fundAmount)) : 'payment'}{' '}
                    securely.
                  </p>
                </div>
              </div>}
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors mt-4" data-id="element-1566">
            Fund Wallet
          </button>
        </form>
      </Modal>
    </div>;
};